import { Component, Vue, Prop, Watch, Ref } from 'vue-property-decorator';
// @ts-ignore pas de d.ts pour la build dist
import Plyr from 'plyr/dist/plyr.js';
import { Storage } from 'aws-amplify';
import { Camera } from '@/models/camera';
import { Project } from '@/models/project';
import { Video } from '@/models/video';
import { saveAs } from 'file-saver';
import ScenarioPlayer from '@/components/scenarioplayer/scenarioplayer.component.vue';
import { State } from 'vuex-class';
import { User } from '@/models/user';
import { Logger } from 'aws-amplify';
const logger = new Logger('TimelapseCard');

@Component({
  components: { ScenarioPlayer },
})
export default class TimelapseCard extends Vue {
  @Prop({ required: true, type: Object }) readonly camera!: Camera;
  @Prop({ required: true, type: Object }) readonly project!: Project;
  @Prop({ type: Object }) readonly lastVideo!: Video | null;
  @Ref('video-player-player') readonly videoPlayerPlayer?: ScenarioPlayer;

  @State('user', { namespace: 'profile' }) private user!: User;

  private visible = true;
  private showVideo = true;

  private videoUrl: string | null = null;
  private player: any = null;
  private videoDownloading = false;

  get plyrOptions(): any {
    const $t = this.$t.bind(this);

    return {
      autoplay: false,
      clickToPlay: true,
      hideControls: true,
      seekTime: 10,
      resetOnEnd: true,
      disableContextMenu: false,
      loadSprite: true,
      iconPrefix: 'plyr',
      iconUrl: 'https://cdn.plyr.io/3.8.3/plyr.svg',
      blankVideo: 'https://cdn.plyr.io/static/blank.mp4',
      controls: [
        'play-large',
        'restart',
        'rewind',
        'play',
        'fast-forward',
        'progress',
        'current-time',
        // 'duration',
        'mute',
        'volume',
        'captions',
        'settings',
        'pip',
        'airplay',
        //'download',
        'fullscreen',
      ],
      tooltips: {
        controls: true,
        seek: true,
      },
      i18n: {
        restart: $t('video.restart').toString(),
        rewind: $t('video.rewind').toString(),
        play: $t('video.play').toString(),
        pause: $t('video.pause').toString(),
        fastForward: $t('video.fastForward').toString(),
        seek: $t('video.seek').toString(),
        seekLabel: $t('video.seekLabel').toString(),
        played: $t('video.played').toString(),
        buffered: $t('video.buffered').toString(),
        currentTime: $t('video.currentTime').toString(),
        duration: $t('video.duration').toString(),
        volume: $t('video.volume').toString(),
        mute: $t('video.mute').toString(),
        unmute: $t('video.unmute').toString(),
        enableCaptions: $t('video.enableCaptions').toString(),
        disableCaptions: $t('video.disableCaptions').toString(),
        settings: $t('video.settings').toString(),
        speed: $t('video.speed').toString(),
        normal: $t('video.normal').toString(),
        quality: $t('video.quality').toString(),
        loop: $t('video.loop').toString(),
        enterFullscreen: $t('video.enterFullscreen').toString(),
        exitFullscreen: $t('video.exitFullscreen').toString(),
        download: $t('video.download').toString(),
        reset: $t('common.reset').toString(),
        disabled: $t('global.need_video').toString(),
        enabled: $t('global.activate').toString(),
        all: $t('global.timelapse').toString(),
        start: $t('global.start').toString(),
        end: $t('global.end').toString(),
      },
    };
  }

  public get isAdmin(): boolean {
    return (
      this.user !== undefined &&
      (this.project.owner.id === this.user.id || this.project.administratorId.includes(this.user.id))
    );
  }

  get hasVideo(): boolean {
    return !!(this.lastVideo && this.lastVideo.key && this.videoUrl);
  }
  get canShare(): boolean {
    return this.hasVideo && !!this.videoUrl;
  }
  get canDownload(): boolean {
    return this.hasVideo && !!this.lastVideo?.key && !this.videoDownloading;
  }

  public mounted(): void {
    this.videoDownloading = false;
    this.onLastVideoChange(this.lastVideo, null);
  }

  public getRotation(): string {
    return this.camera && this.camera.rotate ? `rotate(${this.camera.rotate}deg)` : '';
  }

  private async safeInitPlayer() {
    if (!this.videoUrl) return;

    // Évite les doublons
    if (this.player) {
      this.player.destroy();
      this.player = null;
    }

    await this.$nextTick();

    const el = this.$refs.videoPlayer as HTMLVideoElement | undefined;
    if (!el) return;

    // Création du player Plyr
    this.player = new Plyr(el, this.plyrOptions);

    this.player.source = {
      type: 'video',
      sources: [
        {
          src: this.videoUrl,
          type: 'video/mp4',
        },
      ],
    };
  }

  @Watch('lastVideo')
  public onLastVideoChange(val: Video | null, oldVal: Video | null): void {
    logger.info('onLastVideoChange', { val, oldVal });

    if (this.player) {
      this.player.destroy();
      this.player = null;
    }

    if (this.lastVideo && this.lastVideo.key) {
      Storage.get(this.lastVideo.key)
        .then((url) => {
          this.videoUrl = url as string;
          this.safeInitPlayer();
        })
        .catch((err) => {
          logger.error('Failed to fetch video URL', err);
          this.videoUrl = null;
        });
    } else {
      this.videoUrl = null;
    }
  }

  @Watch('$i18n.locale')
  onLocaleChanged() {
    if (this.player) {
      this.player.destroy();
      this.player = null;
    }
    if (this.videoUrl) {
      this.safeInitPlayer();
    }
  }

  public async onVideoDownload(): Promise<void> {
    if (!this.lastVideo?.key) return;

    this.videoDownloading = true;
    try {
      // 1) Récupère une URL signée S3 sans télécharger en JS
      const signedUrl = (await Storage.get(this.lastVideo.key, { expires: 3600 })) as string;
      const filename = this.makeFilename();

      // 2) Chemin “pro” (Chromium) : File System Access API -> invite immédiate + streaming
      const canUseFS = typeof (window as any).showSaveFilePicker === 'function';
      if (canUseFS) {
        // Ouvre la boîte "Enregistrer sous..." IMMÉDIATEMENT
        const handle = await (window as any).showSaveFilePicker({
          suggestedName: filename,
          types: [{ description: 'Vidéo MP4', accept: { 'video/mp4': ['.mp4'] } }],
        });

        const writable = await handle.createWritable();
        const res = await fetch(signedUrl);
        if (!res.ok || !res.body) throw new Error('Échec réseau pendant le téléchargement');

        // Stream réseau -> disque (sans tout charger en mémoire)
        await res.body.pipeTo(writable);

        this.$bvToast.toast(this.$t('global.download') as string, {
          title: 'Timelapse',
          variant: 'success',
          autoHideDelay: 2500,
        });
        return;
      }

      // 3) Fallback universel : laisser le navigateur gérer le download au plus tôt
      // (peut ouvrir la boîte selon vos réglages et/ou les headers du serveur)
      const a = document.createElement('a');
      a.href = signedUrl;
      a.download = filename; // peut être ignoré en cross-origin selon le navigateur
      a.rel = 'noopener';
      a.target = '_blank'; // évite de quitter l’app si le navigateur ouvre un onglet
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);

      this.$bvToast.toast((this.$t('global.downloadStarting') as string) || 'Téléchargement lancé…', {
        title: 'Timelapse',
        variant: 'info',
        autoHideDelay: 2000,
      });
    } catch (e) {
      logger.error('download failed', e);
      this.$bvToast.toast(this.$t('global.error') as string, {
        title: 'Timelapse',
        variant: 'danger',
        autoHideDelay: 3000,
      });
    } finally {
      this.videoDownloading = false;
    }
  }

  private makeFilename(): string {
    const name = (this.project?.name || 'video').toString().replace(/[^\w-]+/g, '_');
    const date = new Date().toISOString().slice(0, 19).replace(/[:T]/g, '-');
    return `${name}_${date}.mp4`;
  }

  public beforeDestroy(): void {
    if (this.player) {
      this.player.destroy();
      this.player = null;
    }
  }

  onHide(): void {
    this.showVideo = true;
  }

  onShown(): void {
    this.showVideo = true;
    this.$nextTick(() => {
      this.onLastVideoChange(this.lastVideo, null);
    });
  }

  onShowPlayer(): void {
    logger.info({ videoPlayerPlayer: this.videoPlayerPlayer });
    if (this.videoPlayerPlayer !== undefined) {
      // @ts-ignore pas de d.ts
      this.videoPlayerPlayer.show();
    }
  }

  /** Partage: copie l’URL dans le presse-papiers (fallback prompt) */
  public async onShare(): Promise<void> {
    if (!this.videoUrl) return;
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(this.videoUrl);
        this.$bvToast.toast((this.$t('global.copied') as string) || 'Lien copié', {
          title: 'Timelapse',
          variant: 'info',
          autoHideDelay: 2000,
        });
      } else {
        // Fallback
        const ok = window.prompt((this.$t('global.copyLink') as string) || 'Copiez ce lien :', this.videoUrl);
        if (ok !== null) {
          this.$bvToast.toast('Lien prêt à être collé', {
            title: 'Timelapse',
            variant: 'info',
            autoHideDelay: 2000,
          });
        }
      }
    } catch (e) {
      logger.error('share failed', e);
      this.$bvToast.toast(this.$t('global.error') as string, {
        title: 'Timelapse',
        variant: 'danger',
        autoHideDelay: 3000,
      });
    }
  }
}
